import React, { Dispatch, useMemo } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ActionList,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { Contact, ContactEntity, RelationshipType } from 'types/contacts'
import { ActionIds } from 'types/types'
import { contactAtom } from './atoms/contactAtoms'

interface ContactsListProps {
  contacts: Contact[]
  setIsSheetOpen: Dispatch<React.SetStateAction<boolean>>
}

export const ContactsList = ({ contacts, setIsSheetOpen }: ContactsListProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const setContact = useSetAtom(contactAtom)
  const [action, setAction] = useAtom(actionAtom)

  const onDetailsClick = (contact: Contact) => {
    setContact(contact)
    navigate(`${PathNames.CRYPTO_CONTACTS}/${contact?.id}`)
  }

  const onEditClick = (contact: Contact) => {
    setContact(contact)
    setAction({
      ...action,
      actionId: ActionIds?.CONTACT,
      header: t('contacts.updateCryptoContact'),
      onClose: () => {
        setIsSheetOpen(false)
        setContact(null)
      },
      meta: {
        enableCreateInstrument: false,
      },
    })
    setIsSheetOpen(true)
  }

  const actionItemListItems = useMemo(() => {
    return contacts?.map(contact => {
      const {
        id,
        name,
        description,
        relationshipType,
        entity: { type: contactEntityType } = {},
      } = contact || {}

      const isCompany = contactEntityType === ContactEntity.COMPANY
      const isIndividual = contactEntityType === ContactEntity.INDIVIDUAL

      const headerHighlightLeft = (isCompany || isIndividual) && (
        <Highlight
          iconElement={
            isCompany ? (
              <Icon name="BuildingOffice" size="small" aria-label={t('contacts.company')} />
            ) : (
              <Icon name="UserCircle" size="small" aria-label={t('contacts.individual')} />
            )
          }
          type="neutral"
          variant="emphasis"
        >
          {isCompany ? t('contacts.company') : t('contacts.individual')}
        </Highlight>
      )

      const isSelfOwnedCompany = relationshipType === RelationshipType.SELF_OWNED && isCompany

      const headerHighlightRight = isSelfOwnedCompany && (
        <Highlight type="feature" variant="emphasis">
          {t('contacts.myCompany')}
        </Highlight>
      )

      return {
        transparent: false,
        key: id,
        id: id,
        title: name,
        HeaderHighlightLeft: headerHighlightLeft,
        HeaderHighlightRight: headerHighlightRight,
        Avatar: <Avatar type="entity" name={name ?? ''} />,
        BodyPre: description,
        CustomAction: (
          <DropdownMenu
            DropdownMenuItems={[
              ...(canEdit && !viewOnly
                ? [
                    <DropdownMenuItem
                      onClick={() => onEditClick(contact)}
                      key={`action-edit-${id}`}
                      LeadingIcon={<Icon name="SimplePencilCustom" />}
                    >
                      {t('edit')}
                    </DropdownMenuItem>,
                  ]
                : []),
              <DropdownMenuItem
                key={`action-info-${id}`}
                LeadingIcon={<Icon name="Info" />}
                onClick={() => onDetailsClick(contact)}
              >
                {t('details')}
              </DropdownMenuItem>,
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
        onItemClick: () => onDetailsClick(contact),
      }
    })
  }, [contacts])

  return <ActionList id="contacts-list" items={actionItemListItems} />
}
